import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import ChecklistTile from '../../../../../incase-domain/components/checkbox/ChecklistTile';
import FontAndBackgroundColorPicker, {COLOR_FIELDS} from '../../../../../components/slides/highlighted/common/fontAndBackgroundColorPicker';
import SectionTitle from '../../../../../components/elements/common/SectionTitle';
import NumberInput from '../../../../components/NumberInput';
import {ELEMENT} from '../../../../../incase-domain/elements/base/ElementTypes';
import {IPresentationTheme, LineTheme, ShapeTheme, ShapeThemeKey} from '../../../../../incase-domain/presentationTheme';
import {shapeElementTypeToThemeKey} from '../../../../../utils/presentationTheme';
import {buildThemeControlElement} from '../buildThemePreviewElementByType';
import {patchThemeStyles} from '../themeStyle';

interface PresentationThemeShapeElementProps {
    presentationTheme: IPresentationTheme;
    setPresentationTheme: React.Dispatch<React.SetStateAction<IPresentationTheme>>;
    shapeType: ELEMENT;
    shared?: boolean;
}

export default function PresentationThemeShapeElement(props: PresentationThemeShapeElementProps): JSX.Element {
    const {presentationTheme, setPresentationTheme, shapeType, shared = false} = props;
    const {t} = useTranslation();

    const themeKey: ShapeThemeKey = shapeElementTypeToThemeKey[shapeType] ?? 'shapeElement';
    const isLine = themeKey === 'lineElement';

    const shapeTheme = presentationTheme.elementStyles?.shapeElement;
    const lineTheme = presentationTheme.elementStyles?.lineElement;

    const mockElement = useMemo(() => buildThemeControlElement(shapeType, presentationTheme), [presentationTheme, shapeType]);

    const setShapePatch = (patch: Partial<ShapeTheme>): void => {
        setPresentationTheme((prevTheme) => patchThemeStyles(prevTheme, ['shapeElement'], patch));
    };

    const setLinePatch = (patch: Partial<LineTheme>): void => {
        setPresentationTheme((prevTheme) => patchThemeStyles(prevTheme, ['lineElement'], patch));
    };

    const changeHandler = {
        single: (field: string, value: unknown): void => {
            if (field === 'color' && typeof value === 'string') {
                if (shared) {
                    setPresentationTheme((prevTheme) => patchThemeStyles(prevTheme, ['shapeElement', 'lineElement'], {color: value}));
                } else if (isLine) setLinePatch({color: value});
                else setShapePatch({color: value});
            } else if (field === 'fill' && typeof value === 'boolean') {
                setShapePatch({fill: value});
            } else if (field === 'size' && typeof value === 'number') {
                setLinePatch({thickness: value});
            }
        },
    };

    const isFilled = shapeTheme?.fill ?? false;
    const showFill = !isLine || shared;
    const showThickness = isLine || shared;

    return (
        <div className="l-group--form">
            <SectionTitle label={t('designSidepanel.style')} />
            {shared && <p style={{opacity: 0.7, fontSize: '0.85em', margin: '0 0 8px'}}>{t('presentationThemesPage.shapes.sharedHint')}</p>}
            <div className="u-flex u-flex-col u-gap-3">
                {showFill && (
                    <ChecklistTile
                        value={isFilled}
                        label={t('designSidepanel.filled')}
                        onChange={(value) => changeHandler.single('fill', value)}
                    />
                )}
                {showThickness && (
                    <NumberInput
                        label={t('designSidepanel.lineThickness')}
                        step={0.1}
                        min={0.1}
                        value={lineTheme?.thickness ?? 0.1}
                        onChange={(newValue) => changeHandler.single('size', newValue || 0.1)}
                    />
                )}
                <FontAndBackgroundColorPicker
                    highlightedElement={mockElement}
                    highlightedElementChangeHandler={changeHandler}
                    colorFields={[COLOR_FIELDS.BACKGROUND_COLOR]}
                    placeholder={isLine || !isFilled ? t('designSidepanel.lineColor') : t('designSidepanel.color')}
                />
            </div>
        </div>
    );
}
